import { ITEM_SHEET_GRID } from '../data/guide/itemSpriteSheets'
import { BOUNDS_REFERENCE_CELL, itemSpriteBounds } from '../data/guide/itemSpriteBounds'
import { excludedSpriteIds } from '../data/guide/itemSpriteIndex'
import { spriteIndex } from './itemSprites'
import type { WorldData } from '../types/world'

/**
 * Diagnóstico de imágenes de ítems. Cuatro categorías que no se mezclan:
 * - load: la lámina no se descargó o no se decodificó (falla de carga, NO "sin imagen").
 * - frame: recorte o posición incorrectos (encuadre ausente o fuera de la celda, celda vacía en la lámina,
 *   contenido fuera del encuadre, ítem cortado en el borde de su celda).
 * - noIndex: ítem publicado sin entrada en el índice.
 * - variantBase: variante sin imagen específica (usa la del ítem base, rotulada).
 */
export type FrameIssueKind = 'sin-encuadre' | 'encuadre-fuera' | 'vacia' | 'recorte' | 'borde' | 'posicion'
export type FrameIssue = { id: string; sheet: number; column: number; row: number; kind: FrameIssueKind; detail: string }

export const frameIssueLabel: Record<FrameIssueKind, string> = {
  'sin-encuadre': 'Sin encuadre en el archivo (se muestra la celda completa)',
  'encuadre-fuera': 'Encuadre fuera de su celda (se limita a la celda)',
  vacia: 'Celda vacía en la lámina',
  recorte: 'El encuadre deja fuera parte del ítem',
  borde: 'El ítem llega al borde de su celda (puede estar cortado en la lámina)',
  posicion: 'El encuadre no coincide con el contenido de la celda',
}

/** Comprobaciones que no necesitan descargar nada: encuadre presente y dentro de la celda de 256. */
export function staticFrameIssues(): FrameIssue[] {
  const C = BOUNDS_REFERENCE_CELL
  const out: FrameIssue[] = []
  spriteIndex.forEach((cell, id) => {
    const b = itemSpriteBounds[id]
    if (!b) out.push({ id, ...cell, kind: 'sin-encuadre', detail: 'No figura en VANGUARD_ITEMS_ENCUADRE.json' })
    else if (b[0] < 0 || b[1] < 0 || b[2] <= 0 || b[3] <= 0 || b[0] + b[2] > C || b[1] + b[3] > C)
      out.push({ id, ...cell, kind: 'encuadre-fuera', detail: `[${b.join(', ')}]` })
  })
  return out
}

export type SheetAnalysis = {
  sheet: number
  url: string
  width?: number
  height?: number
  /** Descargada y decodificada por completo. */
  decoded: boolean
  loadError?: string
  /** false si el navegador no permite leer los píxeles (CORS); el tamaño sí se mide. */
  analyzable: boolean
  checked: number
  issues: FrameIssue[]
}

const loadDecoded = (url: string, cors: boolean) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image()
    if (cors) img.crossOrigin = 'anonymous'
    img.onload = async () => {
      try {
        if (typeof img.decode === 'function') await img.decode()
        if (!img.naturalWidth || !img.naturalHeight) reject(new Error('Imagen vacía (0 px)'))
        else resolve(img)
      } catch {
        reject(new Error('Se descargó pero no se pudo decodificar'))
      }
    }
    img.onerror = () => reject(new Error('No se pudo descargar'))
    img.src = url
  })

/**
 * Descarga y decodifica una lámina; si se pueden leer sus píxeles, compara el contenido real de cada celda
 * indexada con su encuadre (todo convertido a la celda de referencia de 256, sea cual sea el tamaño servido).
 */
export async function analyzeSheet(sheet: number, url: string): Promise<SheetAnalysis> {
  let img: HTMLImageElement
  let analyzable = true
  try {
    img = await loadDecoded(url, true)
  } catch {
    // Sin CORS: al menos comprobar descarga, decodificación y tamaño.
    analyzable = false
    try {
      img = await loadDecoded(url, false)
    } catch (e) {
      return { sheet, url, decoded: false, loadError: (e as Error).message, analyzable: false, checked: 0, issues: [] }
    }
  }
  const W = img.naturalWidth
  const H = img.naturalHeight
  const base: SheetAnalysis = { sheet, url, width: W, height: H, decoded: true, analyzable, checked: 0, issues: [] }
  if (!analyzable) return base

  let data: Uint8ClampedArray
  try {
    const canvas = document.createElement('canvas')
    canvas.width = W
    canvas.height = H
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return { ...base, analyzable: false }
    ctx.drawImage(img, 0, 0)
    data = ctx.getImageData(0, 0, W, H).data
  } catch {
    return { ...base, analyzable: false }
  }

  // ¿La lámina tiene transparencia? Si no, el fondo se considera blanco.
  let transparent = false
  for (let i = 3; i < data.length; i += 4 * 97) if (data[i] < 250) { transparent = true; break }
  const solid = (p: number) => (transparent ? data[p + 3] > 24 : data[p] + data[p + 1] + data[p + 2] < 720)

  const cw = W / ITEM_SHEET_GRID
  const ch = H / ITEM_SHEET_GRID
  const C = BOUNDS_REFERENCE_CELL
  const sxr = C / cw
  const syr = C / ch
  const TOL = 4
  const issues: FrameIssue[] = []
  let checked = 0

  spriteIndex.forEach((cell, id) => {
    if (cell.sheet !== sheet) return
    checked++
    const px0 = Math.round(cell.column * cw)
    const py0 = Math.round(cell.row * ch)
    const px1 = Math.round((cell.column + 1) * cw)
    const py1 = Math.round((cell.row + 1) * ch)
    let minX = Infinity, minY = Infinity, maxX = -1, maxY = -1
    for (let y = py0; y < py1; y++) {
      const rowOff = y * W
      for (let x = px0; x < px1; x++) {
        if (solid((rowOff + x) * 4)) {
          if (x < minX) minX = x
          if (x > maxX) maxX = x
          if (y < minY) minY = y
          if (y > maxY) maxY = y
        }
      }
    }
    if (maxX < 0) {
      issues.push({ id, ...cell, kind: 'vacia', detail: 'Sin píxeles visibles en su celda' })
      return
    }
    // Contenido real en unidades de la celda de referencia.
    const c = [(minX - px0) * sxr, (minY - py0) * syr, (maxX + 1 - px0) * sxr, (maxY + 1 - py0) * syr].map((v) => Math.round(v))
    const contentTxt = `contenido [${c[0]}, ${c[1]}, ${c[2] - c[0]}, ${c[3] - c[1]}]`
    if (c[0] <= 1 || c[1] <= 1 || c[2] >= C - 1 || c[3] >= C - 1)
      issues.push({ id, ...cell, kind: 'borde', detail: contentTxt })
    const b = itemSpriteBounds[id]
    if (!b) return
    const bx1 = b[0] + b[2]
    const by1 = b[1] + b[3]
    if (c[0] < b[0] - TOL || c[1] < b[1] - TOL || c[2] > bx1 + TOL || c[3] > by1 + TOL) {
      issues.push({ id, ...cell, kind: 'recorte', detail: `${contentTxt} · encuadre [${b.join(', ')}]` })
      return
    }
    const ix = Math.max(0, Math.min(c[2], bx1) - Math.max(c[0], b[0]))
    const iy = Math.max(0, Math.min(c[3], by1) - Math.max(c[1], b[1]))
    const inter = ix * iy
    const union = (c[2] - c[0]) * (c[3] - c[1]) + b[2] * b[3] - inter
    if (union > 0 && inter / union < 0.5)
      issues.push({ id, ...cell, kind: 'posicion', detail: `${contentTxt} · encuadre [${b.join(', ')}]` })
  })

  return { ...base, checked, issues }
}

/** Ítems publicados sin entrada en el índice y variantes que usan la imagen de su ítem base. */
export function catalogImageGaps(w: WorldData) {
  const excluded = new Set(excludedSpriteIds)
  const noIndex = Array.from(w.items.keys()).filter((id) => !spriteIndex.has(id) && !excluded.has(id))
  const variantBase: string[] = []
  const variantNoImage: string[] = []
  w.variants.forEach((v, id) => {
    if (id === v.itemId || spriteIndex.has(id) || excluded.has(v.itemId)) return
    if (spriteIndex.has(v.itemId)) variantBase.push(id)
    else variantNoImage.push(id)
  })
  return { noIndex, variantBase, variantNoImage }
}
